import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { BaseService } from "./base.service";
import { PaginatorState } from "./crud-table/models/paginator.model";
import { GroupingState } from "./crud-table/models/grouping.model";
import {
	ITableState,
	TableResponseModel,
} from "./crud-table/models/table.model";
import { UsersModel } from "./crud-table/models/users.model";
import { ProfileService } from "./profile.service";
import { AuthService } from "./auth.service";
import { USER_TYPE } from "../constants";
import { catchError, finalize, share, shareReplay, tap } from "rxjs/operators";
import {SessionService} from "./session.service";

const DEFAULT_STATE: ITableState = {
	filter: {},
	paginator: new PaginatorState(),
	sorting: {
		column: "email",
		direction: "asc",
	},
	searchTerm: "",
	grouping: new GroupingState(),
	// eslint-disable-next-line no-undefined
	entityId: undefined,
};

export interface IAddress {
	id: number;
	person: string;
	firm: string;
	zipCode: number;
	city: string;
	country: string;
	street: string;
	streetNo: string;
	phoneNumber: number;
}

@Injectable({
	providedIn: "root",
})
export class UsersService extends BaseService<UsersModel> {
	private _users$: BehaviorSubject<UsersModel[]> = new BehaviorSubject<
		Array<UsersModel>
	>([]);
	private _organizationUsers = [];

	private _baseURL: string = "";
	private _userRoutes;
	private _total$ = new BehaviorSubject<number>(0);
	public selectedIds: Array<string> = [];

	constructor(
		private httpClient: HttpClient,
		private sessionService: SessionService,
		private profileService: ProfileService,
		private authService: AuthService
	) {
		super(httpClient);
		this.profileService = profileService;
		this.authService = authService;

		this._userRoutes = this.profileService.userRoutes;

		this.tableState = {
			filter: {},
			paginator: new PaginatorState(),
			sorting: {
				column: "email",
				direction: "asc"
			},
			searchTerm: "",
			grouping: new GroupingState(),
			// eslint-disable-next-line no-undefined
			entityId: undefined,
		};

		this.tableState.paginator.pageSize = 10;

		this.checkUserType();
	}

	get users$() {
		return this._users$.asObservable();
	}

	get total$() {
		return this._total$.asObservable();
	}

	/**
	 * Get Org Users List
	 * @returns void
	 */
	findList(): void {
		this.checkUserType();
		const url =
			this._baseURL +
			environment.API_ROUTES.SUPPLIER.ORGANISATION.USERS.SEARCH;
		this._isLoading$.next(true);
		this._errorMessage.next("");
		const request = this.post(url, this.tableState)
			.pipe(
				shareReplay(),
				share(),
				tap((res: TableResponseModel<any>) => {
					this._users$.next(res.items);
					this._total$.next(res.total);
				}),
				catchError((err) => {
					this._errorMessage.next(err);
					return of({
						items: [],
						total: 0,
					});
				}),
				finalize(() => {
					this._isLoading$.next(false);
				})
			)
			.subscribe();
		this._subscriptions.push(request);
	}

	/**
	 * Handle invite user
	 * @param user : User data object
	 * @returns
	 */
	inviteUser(email: string, role: string): Observable<any> {
		const url = this._baseURL + this._userRoutes.ORGANISATION.USERS.INVITE;
		const invitationRequest = {
			email: email,
			role: role,
			preferredLanguage: this.sessionService.language
		};

		return this.httpClient.post(url, invitationRequest);
	}

	/**
	 * Handle create address
	 * @param item : Address data object
	 * @returns
	 */
	getUser(id: string) {
		const url = this._baseURL + this._userRoutes.ORGANISATION.USERS;
		return this.getById(url, id);
	}

	/**
	 * Handle update sub user
	 * @param item : Address data object
	 * @returns
	 */
	updateUser(id: string, payload): Observable<UsersModel> {
		const url = this._baseURL + "/users/" + id;
		return this.put(url, payload);
	}

	deleteUser(id: string) {
		const URI =
			this._baseURL +
			this._userRoutes.ORGANISATION.ADDRESS.DELETE +
			"/" +
			id;
		this.httpClient.delete(URI).subscribe(() => {
			this.findList();
		});
	}

	get organizationUsers() {
		return this._organizationUsers;
	}

	checkUserType() {
		const currentOrg = this.sessionService.currentOrgId;
		this._userRoutes =
			this.profileService.profileType === USER_TYPE.BUYER
				? environment.API_ROUTES.BUYER
				: environment.API_ROUTES.SUPPLIER;

		if (this._userRoutes) {
			this._baseURL =
				environment.API_ENDPOINT +
				environment.VERSION_V1 +
				this._userRoutes.ORGANISATION.BASE_URL +
				"/" +
				currentOrg;
		}
	}
}
